import { Fragment, useMemo, useState } from 'react';
import { NotFound } from '../../../components/NotFound';
import { useAuth } from '../../../contexts/AuthProvider';
import { AccountTrialBalanceModel, AccountTrialSummaryModel, fetctAccountTrialBalance } from '../../../model/account.model';
import { parseFixed, numberDisplay, toDrCr, getDrCrColor } from '../../../services/formats';
import { PageContainer } from '../../../components/PageContainer';
import { Form, ListGroup } from 'react-bootstrap';
import { Modal } from '../../../components/Modal';
import AccountLedger from './rpt-act-ledger';
import { useUtility } from '../../../contexts/UtilityContext';
import Badge from '../../../components/Badge';

export default function AccountTrialBalance() {
  const { isLoading, showLoader, hideLoader } = useUtility();
  const [summary, setSummary] = useState<AccountTrialSummaryModel>({
    opening_diff: 0,
    trial_diff: 0,
    credit: 0,
    debit: 0,
    amt: 0,
  });
  const [list, setList] = useState<Array<AccountTrialBalanceModel>>([]);
  const [filterList, setFilterList] = useState<Array<AccountTrialBalanceModel>>([]);
  const { authInfo, globalSearch } = useAuth();
  const [selectedAccount, setSelectedAccount] = useState<AccountTrialBalanceModel>();

  const updateSummary = (data: Array<AccountTrialBalanceModel>) => {
    setSummary(prev => ({
      ...prev,
      credit: data.reduce((acc, item) => acc + parseFixed(item.credit), 0),
      debit: data.reduce((acc, item) => acc + parseFixed(item.debit), 0),
    }));
  }

  async function fetchData(inputs: any) {
    try {
      showLoader();
      const res = await fetctAccountTrialBalance(inputs ?? {}, authInfo);
      setList(res.data);
      setFilterList(res.data);
      setSummary(prev => ({
        ...prev,
        opening_diff: res.openingDifference,
      }));
      updateSummary(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      hideLoader();
    }
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value.toLowerCase();
    const filtered = list.filter(item => item.ac_name.toLowerCase().includes(keyword))
    setFilterList(filtered);
    updateSummary(filtered);
  }

  useMemo(() => {
    fetchData(globalSearch.fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearch.fields]);


  return (
    <>
      <PageContainer
        title="Trial Balance"
        showSearchBtn
        body={!isLoading && list.length === 0 ? <NotFound /> : <Fragment>
          {list.length > 0 && <Fragment>
            <Form.Group className="mb-3" controlId="itemSearch">
              <Form.Control
                type="text"
                placeholder="Search by account name..."
                onChange={handleFilterChange}
              />
            </Form.Group>
            <ListGroup>
              <ListGroup.Item>
                <strong>ACCOUNT</strong>
              </ListGroup.Item>
              {filterList.map((item, index) => (
                <ListGroup.Item
                  key={`trial-balance-list-item-${index}`}
                  className={`d-flex justify-content-between gap-3 align-items-center list-group-item-${(index % 2) === 0 ? 'light' : 'dark'}`}
                  onClick={() => setSelectedAccount(item)}
                >
                  <span>{item.ac_name}</span>
                  <Badge color={getDrCrColor(item.amt)} text={toDrCr(item.amt)} />
                </ListGroup.Item>
              ))}

              <ListGroup.Item>
                <div><strong>SUB TOTAL</strong></div>
                <div className="d-flex justify-content-between gap-3 align-items-center">
                  <Badge color="primary" text={`${numberDisplay(summary.debit)} DR`} />
                  <Badge color="warning" text={`${numberDisplay(summary.credit)} CR`} />
                </div>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between gap-3 align-items-center">
                <strong className='text-start'>Opening Diff.<br />{toDrCr(summary.opening_diff)}</strong>
                <strong className='text-end'>Trial Diff.<br />{toDrCr(summary.debit - summary.credit)}</strong>
              </ListGroup.Item>
            </ListGroup>
          </Fragment>}
        </Fragment>
        }
      />
      <Modal
        title={selectedAccount?.ac_name ?? "Account Ledger"}
        show={!!selectedAccount}
        handleClose={() => setSelectedAccount(undefined)}
        fullscreen
      >
        <AccountLedger id={selectedAccount?.ac_id} searchParams={globalSearch} />
      </Modal>
    </>
  );
};